import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguageStore, useLeadCaptureStore } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'wardrobes', i18nKey: 'categoryWardrobes', subKey: 'categoryWardrobesSub' },
  { id: 'doors', i18nKey: 'categoryDoors', subKey: 'categoryDoorsSub' },
  { id: 'kitchens', i18nKey: 'categoryKitchens', subKey: 'categoryKitchensSub' },
  { id: 'other', i18nKey: 'categoryOther', subKey: 'categoryOtherSub' },
];

const LeadCaptureModal = () => {
  const isOpen = useLeadCaptureStore((s) => s.isOpen);
  const source = useLeadCaptureStore((s) => s.source);
  const close = useLeadCaptureStore((s) => s.close);
  const open = useLeadCaptureStore((s) => s.open);
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const lc = t.leadCapture || {};

  // Allow opening via window CustomEvent (escape hatch)
  useEffect(() => {
    const handler = (e) => open(e.detail || 'unknown');
    window.addEventListener('openLeadModal', handler);
    return () => window.removeEventListener('openLeadModal', handler);
  }, [open]);

  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [submitting, setSubmitting] = useState(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setCategory('');
      setForm({ name: '', phone: '', email: '' });
      setSubmitting(false);
    }
  }, [isOpen]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  const selectCategory = (id) => {
    setCategory(id);
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) {
      toast.error(lc.nameRequired || 'Please tell us your name');
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error(lc.phoneOrEmail || 'Phone or email is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/lead-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
          category,
          source: source || 'unknown',
          language,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      trackEvent('lead_form_completed', { source: source || 'lead_modal', category });
      toast.success(lc.success || 'Thank you! Opening Telegram…');
      const url = data.redirect_url || process.env.REACT_APP_TELEGRAM_BOT_URL || 'https://t.me/fedyk_furniture_bot';
      // brief delay so user reads the toast, then redirect
      setTimeout(() => {
        window.open(url, '_blank', 'noopener,noreferrer');
        close();
      }, 800);
    } catch (err) {
      toast.error(lc.error || 'Something went wrong. Please try again.');
      _setSubmitting(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-foreground/40 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
          onClick={close}
          data-testid="lead-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-background w-full max-w-2xl shadow-2xl"
            data-testid="lead-modal"
          >
            <button
              onClick={close}
              className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center hover:bg-muted transition-colors z-10"
              data-testid="lead-modal-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>

            {/* Step indicator */}
            <div className="absolute top-6 left-8 text-xs tracking-[0.25em] uppercase text-muted-foreground">
              {step} / 2
            </div>

            <div className="px-8 py-16 md:px-14 md:py-20">
              {step === 1 ? (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid="lead-step-1"
                >
                  {/* 🎯 Исправлено: Сюда внедрен чистый премиальный логотип с пробелом */}
                  <p className="text-sm font-bold tracking-wide text-[#0A0A0A] mb-4">
                    FEDYK <span className="font-light tracking-normal">Furniture</span>
                  </p>
                  
                  <h2 className="text-3xl md:text-4xl font-extralight tracking-tight mb-4">
                    {lc.step1Title || 'What interests you?'}
                  </h2>
                  <p className="text-muted-foreground font-light mb-10 max-w-md">
                    {lc.step1Sub || 'Choose a collection and we will connect you with our architect'}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => selectCategory(cat.id)}
                        className="text-left p-5 border border-border hover:border-foreground transition-colors duration-300 group"
                        data-testid={`lead-category-${cat.id}`}
                      >
                        <p className="text-base font-light mb-1 group-hover:translate-x-1 transition-transform duration-300">
                          {lc[cat.i18nKey] || cat.id}
                        </p>
                        <p className="text-xs text-muted-foreground tracking-wide">
                          {lc[cat.subKey] || ''}
                        </p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid="lead-step-2"
                >
                  <button
                    onClick={() => setStep(1)}
                    className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground mb-8 transition-colors"
                    data-testid="lead-back-btn"
                  >
                    <ArrowLeft className="w-3 h-3" strokeWidth={1.5} />
                    {lc.back || 'Back'}
                  </button>

                  <p className="text-caption text-muted-foreground mb-4">
                    {lc[CATEGORIES.find(c => c.id === category)?.i18nKey] || category}
                  </p>
                  <h2 className="text-3xl md:text-4xl font-extralight tracking-tight mb-4">
                    {lc.step2Title || 'Your contact details'}
                  </h2>
                  <p className="text-muted-foreground font-light mb-10 max-w-md">
                    {lc.step2Sub || 'We will redirect you to our private Telegram bot for a personal consultation'}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
                    <Input
                      type="text"
                      placeholder={lc.namePh || 'Full name'}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="h-12 rounded-none border-border focus-visible:border-foreground"
                      required
                      autoFocus
                      data-testid="lead-name-input"
                    />
                    <Input
                      type="tel"
                      placeholder={lc.phonePh || 'Phone (preferred contact)'}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="h-12 rounded-none border-border focus-visible:border-foreground"
                      data-testid="lead-phone-input"
                    />
                    <Input
                      type="email"
                      placeholder={lc.emailPh || 'Email (optional)'}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="h-12 rounded-none border-border focus-visible:border-foreground"
                      data-testid="lead-email-input"
                    />
                    <div className="pt-4">
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary w-full sm:w-auto"
                        data-testid="lead-submit-btn"
                      >
                        {submitting ? (lc.submitting || 'Connecting…') : (lc.submit || 'Connect on Telegram')}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LeadCaptureModal;
